import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OFF_COLOR } from "./tabuleData";
import { resolveImg, variantWidth } from "@/lib/api";

// Mapa Tabule (337): stavba = bublina s počtem lidí + okruh, lidé mimo stavby jako kolečka
// s iniciálami, stojící u sebe = šedá bublina s počtem. Vybraná stavba / shluk se rozloží na lidi.
const reduce = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const bubble = (dot, dotText, text) => L.divIcon({
  className: "", iconSize: [0, 0], iconAnchor: [0, 0],
  html: `<div style="position:absolute;left:0;top:0;transform:translate(-50%,-50%);display:flex;align-items:center;gap:6px;height:30px;padding:0 ${text ? 10 : 4}px 0 4px;border-radius:999px;background:#fff;color:#17171a;border:1px solid rgba(0,0,0,.12);box-shadow:0 2px 8px rgba(0,0,0,.25);font:800 12.5px Inter,sans-serif;white-space:nowrap;cursor:pointer"><span style="min-width:22px;height:22px;padding:0 4px;border-radius:999px;background:${dot};color:#fff;display:grid;place-items:center;font-size:11px">${esc(dotText)}</span>${esc(text)}</div>`,
});
const siteIcon = (s) => L.divIcon({
  className: "", iconSize: [24, 24], iconAnchor: [12, 12],
  html: `<div style="width:24px;height:24px;border-radius:7px;background:${s.color};color:#fff;display:grid;place-items:center;font:800 11px Inter,sans-serif;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${esc((s.name || "?")[0].toUpperCase())}</div>`,
});
// Fotka přes iniciály: kroužek v barvě stavby. Fotka je pozadí — když chybí nebo se
// nenačte (vypršelý podpis), nic se neukáže a zůstanou vidět iniciály pod ní.
const pinIcon = (p, sel) => {
  const img = p.photo ? `<span style="position:absolute;inset:0;border-radius:50%;background:url('${esc(resolveImg(p.photo, variantWidth(32)))}') center/cover no-repeat"></span>` : "";
  return L.divIcon({
    className: "", iconSize: [32, 32], iconAnchor: [16, 16],
    html: `<div style="position:relative;width:32px;height:32px;border-radius:50%;background:${p.color};color:#fff;display:grid;place-items:center;font:800 10.5px Inter,sans-serif;border:2.5px solid ${p.photo ? p.color : "#fff"};box-shadow:${sel ? "0 0 0 4px #17171a," : ""}0 0 0 1.5px #fff,0 2px 5px rgba(0,0,0,.35);overflow:hidden">${esc(p.initials)}${img}</div>`,
  });
};

// Okraje okruhu stavby — při výběru stavby je vidět celý okruh, ne jen jeho střed.
const circleCorners = (s) => {
  const dLat = s.radius / 111320, dLng = s.radius / (111320 * Math.cos((s.lat * Math.PI) / 180));
  return [{ lat: s.lat - dLat, lng: s.lng - dLng }, { lat: s.lat + dLat, lng: s.lng + dLng }];
};

export function TabuleMap({ people, places, clusters, pins, group, selId, searching, visibleIds, fitKey, compact, onPick, onGroup, className = "" }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const cb = useRef({});
  cb.current = { onPick, onGroup };

  useEffect(() => {
    mapRef.current = L.map(ref.current, { zoomControl: true, attributionControl: false }).setView([50.0755, 14.4378], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapRef.current);
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    (map._tb || []).forEach((l) => map.removeLayer(l));
    map._tb = [];
    const add = (l) => { l.addTo(map); map._tb.push(l); return l; };
    places.filter((s) => s.lat != null && s.lng != null).forEach((s) => {
      const on = group === s.key;
      add(L.circle([s.lat, s.lng], { radius: s.radius, color: s.color, weight: 1.5, dashArray: "5 4", fillColor: s.color, fillOpacity: on ? 0.18 : 0.08, interactive: false }));
      // vybraná stavba: jen malý čtvereček (lidé se rozloží kolem), jinak bublina s počtem
      add(L.marker([s.lat, s.lng], { icon: on ? siteIcon(s) : bubble(s.color, compact ? s.count : (s.name || "?")[0].toUpperCase(), compact ? "" : `${s.name} · ${s.count}`), title: s.name, zIndexOffset: on ? 100 : 500 }))
        .on("click", () => cb.current.onGroup(s.key));
    });
    clusters.filter((c) => c.key !== group).forEach((c) => {
      add(L.marker([c.lat, c.lng], { icon: bubble(OFF_COLOR, c.ids.length, compact ? "" : "lidí"), title: `${c.ids.length} lidí na jednom místě`, zIndexOffset: 400 }))
        .on("click", () => cb.current.onGroup(c.key));
    });
    people.filter((p) => pins.has(p.id)).forEach((p) => {
      const sel = p.id === selId;
      add(L.marker([p.lat, p.lng], { icon: pinIcon(p, sel), title: p.name, zIndexOffset: sel ? 1000 : 600 })).on("click", () => cb.current.onPick(p.id));
    });
  }, [people, places, clusters, pins, group, selId, compact]);

  // Přiblížení jen při změně výběru / filtru / hledání (fitKey) — ne při obnovení dat
  // každých 30 s, jinak by mapa uživateli ujížděla, když si ji posune.
  const latest = useRef({});
  latest.current = { people, places, clusters, group, selId, searching, visibleIds };
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const { people: pe, places: pl, clusters: cl, group: g, selId: sel, searching: sq, visibleIds: vis } = latest.current;
    const gps = pe.filter((p) => p.hasGps);
    const selP = gps.find((p) => p.id === sel);
    const site = pl.find((s) => s.key === g && s.lat != null);
    const pts = selP ? [selP]
      : site ? [...circleCorners(site), ...gps.filter((p) => p.group === g)]
      : g ? gps.filter((p) => p.group === g || p.cluster === g)
      : sq ? gps.filter((p) => vis.has(p.id))
      : [...gps, ...pl.filter((s) => s.lat != null), ...cl];
    const ll = pts.map((p) => [p.lat, p.lng]);
    const opts = { animate: !reduce() };
    if (ll.length === 1) map.setView(ll[0], selP ? 17 : 16, opts);
    else if (ll.length > 1) map.fitBounds(ll, { padding: [44, 44], maxZoom: g ? 19 : 17, ...opts });
    setTimeout(() => map.invalidateSize(), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  return <div ref={ref} data-testid="tabule-map" className={`isolate rounded-[20px] overflow-hidden border border-border ${className}`} />;
}
